import React from 'react'
import styled from 'styled-components'
import moment from 'moment'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import BusinessIcon from '@mui/icons-material/Business'
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined'
import FolderIcon from '@mui/icons-material/Folder'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import BlockIcon from '@mui/icons-material/Block'
import AddTaskOutlinedIcon from '@mui/icons-material/AddTaskOutlined'
import { PRIVATE_PATHS, redirect } from 'utils/paths'
import { useApproveFromPill, canApproveFromPill } from 'features/taskManagement/useApproveFromPill'
import TaskTypeBadge from 'features/taskManagement/components/TaskTypeBadge'
import TaskCadenceBadge from 'features/taskManagement/components/TaskCadenceBadge'
import { taskStatusMeta } from 'features/taskManagement/taskStatusConstants'

const TaskCard = ({ task, onClick }) => {
  const {
    title,
    status,
    assignees = [],
    building,
    project,
    team,
    deadline,
    createdAt,
    progress = 0,
    isOverdue,
    blockedBy = [],
    hasIncompleteBlockers
  } = task

  const displayStatus = isOverdue && status !== 'completed' ? 'overdue' : status
  const statusInfo = taskStatusMeta(displayStatus)
  const [approveFromPill, approveConfirmNode] = useApproveFromPill()

  const formatDeadline = (date) => {
    if (!date) return null
    const deadlineDate = moment(date)
    const today = moment().startOf('day')
    const diffDays = deadlineDate.diff(today, 'days')

    if (diffDays < 0) return `${Math.abs(diffDays)}d overdue`
    if (diffDays === 0) return 'Due today'
    if (diffDays === 1) return 'Due tomorrow'
    if (diffDays <= 7) return `${diffDays}d left`
    return deadlineDate.format('DD MMM YYYY')
  }

  return (
    <CardWrapper onClick={() => onClick(task)} $domainColour={task.taskDomain?.colour}>
      <CardHeader>
        <HeaderLeft>
          {canApproveFromPill(task) ? (
            <StatusBadge
              $color={statusInfo.color}
              $bg={statusInfo.bg}
              $clickable
              role="button"
              tabIndex={0}
              onClick={(e) => approveFromPill(task, e)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); approveFromPill(task, e) } }}
              title="Click to approve and complete this task"
            >
              {statusInfo.label}<PillTick aria-hidden="true">✓</PillTick>
            </StatusBadge>
          ) : (
            <StatusBadge $color={statusInfo.color} $bg={statusInfo.bg}>
              {statusInfo.label}
            </StatusBadge>
          )}
          {hasIncompleteBlockers && (
            <BlockedIndicator title={`Blocked by ${blockedBy.length} task(s)`}>
              <BlockIcon />
            </BlockedIndicator>
          )}
        </HeaderLeft>
        {progress > 0 && status !== 'completed' && (
          <ProgressRing progress={progress}>
            <svg viewBox="0 0 36 36">
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#e0e0e0"
                strokeWidth="3"
              />
              <path
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#4caf50"
                strokeWidth="3"
                strokeDasharray={`${progress}, 100`}
              />
            </svg>
            <span>{progress}%</span>
          </ProgressRing>
        )}
      </CardHeader>

      <CardTitle>{title}</CardTitle>

      <CardMeta>
        <TaskTypeBadge task={task} />
        <TaskCadenceBadge task={task} />
        {task.taskDomain && (
          <DomainChip $colour={task.taskDomain.colour}>
            <DomainSwatch $colour={task.taskDomain.colour} />
            <span>{task.taskDomain.short || task.taskDomain.name}</span>
          </DomainChip>
        )}
        {building && (
          <MetaItem>
            <BusinessIcon />
            <span>{building.name}</span>
          </MetaItem>
        )}
        {(() => {
          const unitList = task.units && task.units.length > 0 ? task.units : (task.unit ? [task.unit] : [])
          if (unitList.length === 0) return null
          return (
            <MetaItem>
              <MeetingRoomOutlinedIcon />
              <span>{unitList[0].name}{unitList.length > 1 ? ` +${unitList.length - 1}` : ''}</span>
            </MetaItem>
          )
        })()}
        {project && (
          <MetaItem>
            <FolderIcon />
            <span>{project.name}</span>
          </MetaItem>
        )}
        {team && (
          <MetaItem>
            <GroupOutlinedIcon />
            <span>{team.name}</span>
          </MetaItem>
        )}
        {task.followUpFor && (
          <FollowUpLink
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              redirect(PRIVATE_PATHS.taskDetails.replace(':id', task.followUpFor.id))
            }}
            title={`Follow-up of "${task.followUpFor.title}". Open it`}
          >
            <AddTaskOutlinedIcon />
            <span>Follow-up of {task.followUpFor.title}</span>
          </FollowUpLink>
        )}
      </CardMeta>

      <CardFooter>
        <AssigneesWrapper>
          {assignees.length > 0 ? (
            <>
              <PersonOutlineIcon />
              <span>
                {assignees.length === 1
                  ? `${assignees[0].firstName || ''} ${assignees[0].lastName || ''}`.trim() || '?'
                  : `${`${assignees[0].firstName || ''} ${assignees[0].lastName || ''}`.trim() || '?'} +${assignees.length - 1}`}
              </span>
            </>
          ) : (
            <>
              <PersonOutlineIcon />
              <span className="unassigned">Unassigned</span>
            </>
          )}
        </AssigneesWrapper>

        <FooterMeta>
          {createdAt && (
            <CreatedClock title={`Created ${moment(createdAt).format('DD MMM YYYY')}`}>
              <AccessTimeIcon />
            </CreatedClock>
          )}
          {deadline && (
            <DeadlineWrapper $isOverdue={isOverdue}>
              <CalendarTodayIcon />
              <span>{formatDeadline(deadline)}</span>
            </DeadlineWrapper>
          )}
        </FooterMeta>
      </CardFooter>
      {approveConfirmNode}
    </CardWrapper>
  )
}

export default TaskCard

const CardWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  ${({ $domainColour }) => $domainColour && `border-left: 3px solid ${$domainColour};`}

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const BlockedIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #fff7e6;
  color: #fa8c16;
  cursor: help;

  svg {
    font-size: 14px;
  }
`

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ $color }) => $color};
  background-color: ${({ $bg }) => $bg};
  ${({ $clickable }) => $clickable ? `
    cursor: pointer;
    &:hover { filter: brightness(0.95); }
    &:focus-visible { outline: 2px solid currentColor; outline-offset: 1px; }
  ` : ''}
`

const PillTick = styled.span`
  font-weight: 800;
  line-height: 1;
`

const ProgressRing = styled.div`
  position: relative;
  width: 36px;
  height: 36px;

  svg {
    width: 100%;
    height: 100%;
    transform: rotate(-90deg);
  }

  span {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 9px;
    font-weight: 600;
    color: #4caf50;
  }
`

const CardTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin: 0 0 12px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey[800]};

  svg {
    font-size: 16px;
    opacity: 0.6;
  }
`

const FollowUpLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  min-width: 0;
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 11.5px;
  font-weight: 500;
  color: #0e7490;
  cursor: pointer;

  svg { font-size: 14px !important; opacity: 1; flex-shrink: 0; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  &:hover { text-decoration: underline; }
`

const DomainChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  color: ${({ $colour }) => $colour || '#475569'};
  background-color: ${({ $colour }) => `${$colour || '#64748b'}14`};
`

const DomainSwatch = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $colour }) => $colour || '#64748b'};
`

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
`

const AssigneesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    font-size: 18px;
    opacity: 0.6;
  }

  .unassigned {
    color: ${({ theme }) => theme.colors.grey[700]};
    font-style: italic;
  }
`

const DeadlineWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ $isOverdue, theme }) =>
    $isOverdue ? '#f44336' : theme.colors.grey[800]};

  svg {
    font-size: 14px;
  }
`

const FooterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const CreatedClock = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.grey[500] || '#9ca3af'};
  cursor: help;

  svg {
    font-size: 14px;
  }
`
